import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const fmt = (n) => Number(n || 0).toLocaleString();

const REWARD_OPTIONS = [
  { title: "Free Metro Delivery", pts: 1000 }, { title: "$25 Rental Credit", pts: 2500 },
  { title: "Vehicle Upgrade", pts: 4000 }, { title: "Free Rental Day", pts: 15000 },
];
const TIER_NAMES = ["Member", "Silver Driver", "Gold Driver", "Black Card"];

export default function ClubAdmin({ token }) {
  const [a, setA] = useState(null);
  const [members, setMembers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [cf, setCf] = useState({ title: "", desc: "", bonus_points: "", ends: "", starts: "", double_points: false });
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/drivers-club/analytics`, auth(token)).then((r) => setA(r.data)).catch(() => {});
    axios.get(`${API}/drivers-club/members`, auth(token)).then((r) => setMembers(r.data)).catch(() => {});
    axios.get(`${API}/drivers-club/challenges`).then((r) => setChallenges(r.data)).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const addChallenge = async () => {
    if (!cf.title.trim()) { toast.error("Give the challenge a title."); return; }
    try {
      await axios.post(`${API}/drivers-club/challenges`, { ...cf, bonus_points: parseInt(cf.bonus_points) || 0 }, auth(token));
      toast.success("Challenge live — members see it on their dashboard now.");
      setCf({ title: "", desc: "", bonus_points: "", ends: "", starts: "", double_points: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save."); }
  };

  const delChallenge = async (id) => {
    await axios.delete(`${API}/drivers-club/challenges/${id}`, auth(token)).catch(() => {});
    load();
  };

  const redeem = async (m) => {
    const reward = window.prompt(`Redeem for ${m.name} (${fmt(m.points)} pts available).\nReward name:`, "Vehicle Upgrade");
    if (!reward) return;
    const pts = parseInt(window.prompt("Points to deduct:", "4000") || "0");
    if (!pts) return;
    try {
      const { data } = await axios.post(`${API}/drivers-club/redeem`, { member_id: m.id, reward, points: pts }, auth(token));
      toast.success(`Redeemed — new balance ${fmt(data.new_balance)} pts.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not redeem."); }
  };

  const bonus = async (m) => {
    const pts = parseInt(window.prompt(`Award bonus points to ${m.name}:`, "500") || "0");
    if (!pts) return;
    const reason = window.prompt("Reason (shown in records):", "Bonus challenge") || "";
    try {
      await axios.post(`${API}/drivers-club/points`, { member_id: m.id, points: pts, reason }, auth(token));
      toast.success("Points awarded.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not award."); }
  };

  if (!a) return <div className="flex items-center gap-2 text-white/50 py-10 justify-center"><Loader2 className="animate-spin" size={17} /> Loading Drivers Club…</div>;

  const kpis = [
    ["Total Members", a.total_members], ["New This Week", a.new_this_week], ["New This Month", a.new_this_month],
    ["Active (90d)", a.active_members_90d], ["Points Issued", fmt(a.total_points_issued)],
    ["Rewards Redeemed", `${a.rewards_redeemed.count} (${fmt(a.rewards_redeemed.points)} pts)`],
    ["Referral Bookings", a.referral_bookings], ["Member Revenue", `$${fmt(a.member_revenue)}`],
  ];

  const filtered = members.filter((m) => !search
    || `${m.name} ${m.email} ${m.phone}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="club-admin">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="club-analytics">
        {kpis.map(([l, v]) => (
          <div key={l} className="bg-white/[0.03] border border-white/10 rounded-lg p-3">
            <p className="text-white/40 text-[10px] uppercase tracking-wider">{l}</p>
            <p className="font-display font-extrabold text-lg mt-0.5 text-white">{v}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {[["Top 10 by Referrals", a.top_by_referrals, (r) => `${r.referrals} referrals`],
          ["Top 10 by Lifetime Spend", a.top_by_spend, (r) => `$${fmt(r.spend)}`]].map(([title, rows, val]) => (
          <div key={title} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
            <h4 className="text-white font-semibold text-sm mb-3">{title}</h4>
            {rows.length === 0 ? <p className="text-white/35 text-xs">Nothing yet.</p> : rows.map((r, i) => (
              <div key={r.id} className="flex items-center gap-3 text-sm py-1.5 border-t border-white/[0.05] text-white/75">
                <span className="text-white/30 w-5">{i + 1}.</span>
                <span className="flex-1 font-semibold truncate">{r.name}</span>
                <span className="text-[10px] uppercase text-white/40">{r.tier}</span>
                <span className="font-bold text-[#F2B705]">{val(r)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="club-challenges-admin">
        <h4 className="text-white font-semibold text-sm mb-1">Bonus Challenges</h4>
        <p className="text-white/40 text-xs mb-3">Shown live on every member's dashboard — award the points with the "+ Pts" button when a member completes one.</p>
        <div className="flex flex-wrap items-end gap-2">
          <input data-testid="club-challenge-title" placeholder="Double Points Weekend" value={cf.title} onChange={(e) => setCf((p) => ({ ...p, title: e.target.value }))} className="flex-1 min-w-44 bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-white text-xs" />
          <input data-testid="club-challenge-desc" placeholder="description (optional)" value={cf.desc} onChange={(e) => setCf((p) => ({ ...p, desc: e.target.value }))} className="flex-1 min-w-44 bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-white text-xs" />
          <input data-testid="club-challenge-points" type="number" placeholder="bonus pts" value={cf.bonus_points} onChange={(e) => setCf((p) => ({ ...p, bonus_points: e.target.value }))} className="w-24 bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-white text-xs" />
          <input data-testid="club-challenge-starts" type="date" title="starts" value={cf.starts} onChange={(e) => setCf((p) => ({ ...p, starts: e.target.value }))} className="bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-white text-xs" />
          <input data-testid="club-challenge-ends" type="date" title="ends" value={cf.ends} onChange={(e) => setCf((p) => ({ ...p, ends: e.target.value }))} className="bg-white/[0.04] border border-white/15 rounded px-3 py-2 text-white text-xs" />
          <label className="flex items-center gap-1.5 text-xs text-white/70 cursor-pointer">
            <input data-testid="club-challenge-double" type="checkbox" checked={cf.double_points} onChange={(e) => setCf((p) => ({ ...p, double_points: e.target.checked }))} className="w-4 h-4 accent-[#F2B705]" />
            2x points
          </label>
          <button data-testid="club-challenge-add" onClick={addChallenge} className="flex items-center gap-1 bg-[#F2B705] text-black text-xs font-bold px-4 py-2 rounded-md"><Plus size={13} /> Launch</button>
        </div>
        {challenges.map((c) => (
          <div key={c.id} className="flex items-center gap-3 text-xs text-white/60 bg-white/[0.02] rounded px-3 py-2 mt-2">
            <span className="font-semibold text-white/85">{c.title}</span>
            <span className="flex-1 truncate">{c.desc}</span>
            {c.bonus_points > 0 && <span className="text-[#F2B705] font-bold">+{fmt(c.bonus_points)} pts</span>}
            {c.ends && <span>ends {c.ends}</span>}
            <button data-testid={`club-challenge-del-${c.id}`} onClick={() => delChallenge(c.id)} className="text-white/30 hover:text-red-400"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>

      <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5" data-testid="club-members-table">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h4 className="text-white font-semibold text-sm">Members ({filtered.length})</h4>
          <input data-testid="club-member-search" placeholder="Search name, email or phone…" value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-64 bg-white/[0.04] border border-white/15 focus:border-[#F2B705] rounded px-3 py-2 text-white text-xs outline-none" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-white/40 text-[11px] uppercase tracking-wider text-left">
              <th className="py-2 pr-3">Member</th><th className="py-2 pr-3">Tier</th><th className="py-2 pr-3">Balance</th>
              <th className="py-2 pr-3">Referrals</th><th className="py-2 pr-3">Spend</th><th className="py-2 pr-3">Joined</th><th className="py-2">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((m) => (
                <MemberRow key={m.id} m={m} token={token} open={openId === m.id}
                  onToggle={() => setOpenId(openId === m.id ? null : m.id)} onChanged={load} />
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-white/35 text-xs">{search ? "No members match your search." : "No members yet — share /club to start signups."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MemberRow({ m, token, open, onToggle, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [pts, setPts] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open && !detail) {
      axios.get(`${API}/drivers-club/member/${m.id}/detail`, auth(token)).then((r) => setDetail(r.data)).catch(() => {});
    }
  }, [open, detail, m.id, token]);

  const refresh = () => { setDetail(null); onChanged(); };

  const redeem = async (r) => {
    try {
      const { data } = await axios.post(`${API}/drivers-club/redeem`, { member_id: m.id, reward: r.title, points: r.pts }, auth(token));
      toast.success(`${r.title} redeemed — new balance ${fmt(data.new_balance)} pts.`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not redeem."); }
  };

  const award = async (sign) => {
    const n = (parseInt(pts) || 0) * sign;
    if (!n) { toast.error("Enter a points amount."); return; }
    try {
      await axios.post(`${API}/drivers-club/points`, { member_id: m.id, points: n, reason }, auth(token));
      toast.success(`${n > 0 ? "+" : ""}${fmt(n)} points applied.`);
      setPts(""); setReason("");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not apply."); }
  };

  const setTier = async (tier) => {
    try {
      await axios.patch(`${API}/drivers-club/member/${m.id}/tier`, { tier }, auth(token));
      toast.success(tier ? `Tier locked to ${tier}.` : "Tier back to automatic.");
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update tier."); }
  };

  return (
    <>
      <tr className="border-t border-white/[0.06] text-white/80">
        <td className="py-2 pr-3"><span className="font-semibold">{m.name}</span><span className="block text-white/35 text-[11px]">{m.phone} · {m.email}</span></td>
        <td className="py-2 pr-3 text-[11px] uppercase text-white/50">{m.tier}</td>
        <td className="py-2 pr-3 font-bold text-[#F2B705]">{fmt(m.points)}</td>
        <td className="py-2 pr-3">{m.referrals}</td>
        <td className="py-2 pr-3">${fmt(m.spend)}</td>
        <td className="py-2 pr-3 text-white/40 text-xs">{String(m.joined_at || "").slice(0, 10)}</td>
        <td className="py-2">
          <button data-testid={`club-member-manage-${m.id}`} onClick={onToggle}
            className={`text-xs rounded px-3 py-1.5 font-bold ${open ? "bg-[#F2B705] text-black" : "border border-white/20 hover:bg-white hover:text-black"} transition-colors`}>
            {open ? "Close" : "Manage"}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="border-t border-white/[0.04]">
          <td colSpan={7} className="py-4 px-2">
            {!detail ? <div className="flex items-center gap-2 text-white/40 text-xs"><Loader2 size={13} className="animate-spin" /> Loading member…</div> : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid={`club-member-detail-${m.id}`}>
                <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4">
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mb-2">One-Click Redeem</p>
                  {REWARD_OPTIONS.map((r) => (
                    <button key={r.title} data-testid={`club-redeem-${m.id}-${r.pts}`} onClick={() => redeem(r)} disabled={detail.points.balance < r.pts}
                      className="w-full flex justify-between items-center text-xs border border-white/15 rounded px-3 py-2 mb-1.5 disabled:opacity-30 hover:border-[#F2B705] transition-colors">
                      <span>{r.title}</span><span className="font-bold text-[#F2B705]">{fmt(r.pts)} pts</span>
                    </button>
                  ))}
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mt-4 mb-2">Points Adjustment</p>
                  <div className="flex gap-1.5">
                    <input data-testid={`club-pts-input-${m.id}`} type="number" placeholder="pts" value={pts} onChange={(e) => setPts(e.target.value)} className="w-20 bg-white/[0.04] border border-white/15 rounded px-2 py-1.5 text-white text-xs" />
                    <input data-testid={`club-pts-reason-${m.id}`} placeholder="reason (required — audit trail)" value={reason} onChange={(e) => setReason(e.target.value)} className={`flex-1 bg-white/[0.04] border rounded px-2 py-1.5 text-white text-xs ${reason.trim() ? "border-white/15" : "border-amber-500/40"}`} />
                    <button data-testid={`club-pts-add-${m.id}`} onClick={() => award(1)} className="bg-emerald-500/90 text-black text-xs font-bold px-2.5 rounded">+</button>
                    <button data-testid={`club-pts-remove-${m.id}`} onClick={() => award(-1)} className="bg-red-500/80 text-white text-xs font-bold px-2.5 rounded">−</button>
                  </div>
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mt-4 mb-1.5">Tier Override</p>
                  <select data-testid={`club-tier-select-${m.id}`} value={detail.tier_override || ""} onChange={(e) => setTier(e.target.value)}
                    className="w-full bg-[#0C0C0C] border border-white/15 rounded px-2 py-1.5 text-white text-xs">
                    <option value="">Automatic (by lifetime points)</option>
                    {TIER_NAMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4">
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mb-2">Rental History ({detail.rentals.length})</p>
                  {detail.rentals.length === 0 ? <p className="text-white/30 text-xs">No rentals yet.</p> : detail.rentals.map((r, i) => (
                    <div key={i} className="flex justify-between text-xs text-white/65 py-1 border-t border-white/[0.05]">
                      <span className="font-semibold">{r.vehicle}</span><span>{r.pickup_date}</span><span>${fmt(r.total)}</span>
                    </div>
                  ))}
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mt-4 mb-2">Points Breakdown</p>
                  {Object.entries(detail.points.breakdown).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs text-white/60 py-0.5"><span className="capitalize">{k.replaceAll("_", " ")}</span><span className={v < 0 ? "text-red-400" : "text-[#F2B705]"}>{v > 0 ? "+" : ""}{fmt(v)}</span></div>
                  ))}
                </div>
                <div className="bg-white/[0.02] border border-white/10 rounded-lg p-4">
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mb-2">Referral History ({detail.referral_history.length})</p>
                  {detail.referral_history.length === 0 ? <p className="text-white/30 text-xs">No referrals yet.</p> : detail.referral_history.map((r, i) => (
                    <div key={i} className="flex justify-between text-xs text-white/65 py-1 border-t border-white/[0.05]">
                      <span className="font-mono">{r.code}</span><span>{String(r.at || "").slice(0, 10)}</span><span className="text-[#F2B705]">+500</span>
                    </div>
                  ))}
                  <p className="text-white/50 text-[10px] uppercase tracking-wider mt-4 mb-2">Redemptions ({detail.redemptions.length})</p>
                  {detail.redemptions.length === 0 ? <p className="text-white/30 text-xs">Nothing redeemed yet.</p> : detail.redemptions.map((r, i) => (
                    <div key={i} className="flex justify-between text-xs text-white/65 py-1 border-t border-white/[0.05]">
                      <span>{r.reward}</span><span className="text-red-400">−{fmt(r.points)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
